/**
 * Centered icon + headline + error detail + retry placeholder used
 * wherever a surface failed to load — directory listings, session
 * lists, any in-pane data fetch.
 *
 * Visual sibling of :class:`EmptyState`: same recipe (centered,
 * ``flex-1``, capped subtext width), just with a tinted destructive
 * icon tile and a built-in retry CTA. Drop one in to replace ad-hoc
 * inline red banners so a failed list reads as the same product as
 * an empty list — different terminal state, same shape.
 *
 * The retry button manages its own in-flight state: pass ``onRetry``
 * (sync or async) and the button disables and spins until the call
 * resolves, so a click that re-issues a still-failing request gives
 * unmistakable visual feedback instead of looking like a no-op.
 *
 * Use the ``children`` slot for an additional escape-hatch CTA
 * (e.g. "Switch project") that should sit next to the retry button.
 */

import { AlertTriangle, type LucideIcon, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';

export interface ErrorStateProps {
  /** Lucide icon rendered above the headline (default ``AlertTriangle``). */
  icon?: LucideIcon;
  /** Short, sentence-case headline — the "what failed". */
  title: string;
  /** Optional supporting copy — typically the underlying error message. */
  description?: React.ReactNode;
  /**
   * Click handler for the built-in Retry button. May return a Promise;
   * the button stays disabled (with a spinner) until it resolves.
   * Omit to hide the Retry button entirely (rare — only for terminal
   * states where retrying would be meaningless).
   */
  onRetry?: () => void | Promise<void>;
  /** Override the Retry button label. */
  retryLabel?: string;
  /** Extra CTA(s) rendered next to the Retry button. */
  children?: React.ReactNode;
  className?: string;
}

export function ErrorState({
  icon: Icon = AlertTriangle,
  title,
  description,
  onRetry,
  retryLabel = 'Retry',
  children,
  className,
}: ErrorStateProps) {
  const [retrying, setRetrying] = React.useState(false);

  const handleRetry = React.useCallback(async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  }, [onRetry, retrying]);

  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center px-6 py-10 text-center', className)}>
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400">
        <Icon aria-hidden="true" className="size-5" strokeWidth={2.25} />
      </div>
      <p className="font-semibold text-[15px] text-gray-700 dark:text-gray-200">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm break-words text-[13px] text-gray-500 leading-relaxed dark:text-gray-400">
          {description}
        </p>
      )}
      {(onRetry || children) && (
        <div className="mt-4 flex items-center gap-2">
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={retrying}
              onClick={handleRetry}
              className="gap-1.5"
            >
              {retrying ? <Spinner size="size-3.5" /> : <RotateCcw className="size-3.5" strokeWidth={2.25} />}
              <span>{retryLabel}</span>
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
